<template>
    <article
        :ref="setCommentElement"
        class="comment"
        :class="{ compact: props.compact, 'group-parent': props.groupParent, reply: props.isReply }"
        :tabindex="props.canComment ? 0 : undefined"
    >
        <div class="comment-meta">
            <strong>{{ formatCommentAuthorName(props.comment.authorName) }}</strong>
            <div class="comment-meta-actions" :class="{ 'has-actions': props.canComment }">
                <div v-if="props.canComment" class="comment-action-controls">
                    <button class="comment-action-button" type="button" aria-label="Reply" v-tooltip="'Reply'" @click="emit('reply', props.comment, props.parentCommentId ?? props.comment.commentId)">
                        <font-awesome-icon :icon="['fas', 'reply']" />
                    </button>
                    <button v-if="props.comment.canModify" class="comment-action-button" type="button" aria-label="Edit" v-tooltip="'Edit'" @click="emit('edit', props.comment)">
                        <font-awesome-icon :icon="['fas', 'pen']" />
                    </button>
                    <button v-if="props.comment.canModify" class="comment-action-button" type="button" aria-label="Delete" v-tooltip="'Delete'" :disabled="props.deleting" @click="emit('delete', props.comment.commentId)">
                        <font-awesome-icon :icon="['fas', 'trash-can']" />
                    </button>
                </div>
                <div class="comment-date-meta">
                    <span>{{ formatCommentDate(props.comment.createdAt) }}</span>
                    <span v-if="isCommentEdited(props.comment)" class="comment-edited" v-tooltip="`Edited ${formatFullDateTime(props.comment.updatedAt)}`">edited</span>
                </div>
            </div>
        </div>
        <slot name="editor" v-if="props.isEditing" />
        <div v-else class="comment-body-row">
            <div class="comment-body-wrapper" :class="{ truncated: props.isTruncated }">
                <div
                    :ref="setBodyElement"
                    class="comment-body"
                    v-html="sanitizeRichTextHtml(props.comment.bodyHtml)"
                />
            </div>
            <div class="comment-controls-row">
                <button
                    v-if="props.isTruncated"
                    class="comment-view-full"
                    type="button"
                    @click="emit('open-full', props.comment.commentId)"
                >
                    View full comment
                    <font-awesome-icon :icon="['fas', 'arrow-right']" />
                </button>
                <div v-if="props.isTruncated" class="comment-controls-spacer" />
                <comment-vote-control
                    v-if="props.loggedIn"
                    class="comment-vote-control"
                    :vote-count="props.comment.voteCount"
                    :current-user-vote="props.comment.currentUserVote"
                    :disabled="props.isVotePending"
                    @vote="vote => emit('vote', props.comment.commentId, vote)"
                />
            </div>
        </div>
    </article>
</template>

<script setup lang="ts">
import CommentVoteControl from '@/components/common/CommentVoteControl.vue';
import { IDocumentComment, TDocumentCommentVote } from '@/interfaces/deltaGpt/IDocumentComment.ts';
import { sanitizeRichTextHtml } from '@/utils/htmlUtils.ts';

const props = withDefaults(defineProps<{
    comment: IDocumentComment;
    parentCommentId?: string | null;
    isReply?: boolean;
    compact?: boolean;
    groupParent?: boolean;
    canComment: boolean;
    loggedIn: boolean;
    isEditing?: boolean;
    isTruncated: boolean;
    isVotePending: boolean;
    deleting: boolean;
}>(), {
    parentCommentId: null,
    isReply: false,
    compact: false,
    groupParent: false,
    isEditing: false,
});

const emit = defineEmits<{
    reply: [comment: IDocumentComment, parentCommentId: string];
    edit: [comment: IDocumentComment];
    delete: [commentId: string];
    vote: [commentId: string, vote: TDocumentCommentVote];
    'open-full': [commentId: string];
    'body-element': [commentId: string, element: unknown];
    'comment-element': [commentId: string, element: unknown];
}>();

defineOptions({ name: 'DocumentCommentCard' });

function setBodyElement (element: unknown): void {
    emit('body-element', props.comment.commentId, element);
}

function setCommentElement (element: unknown): void {
    emit('comment-element', props.comment.commentId, element);
}

function formatCommentDate (date?: string | null): string {
    if (!date) return 'No date';

    const parsedDate = new Date(date);
    const today = new Date();
    const isToday = parsedDate.getDate() === today.getDate()
        && parsedDate.getMonth() === today.getMonth()
        && parsedDate.getFullYear() === today.getFullYear();

    if (isToday) {
        return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' }).format(parsedDate);
    }

    return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(parsedDate);
}

function formatFullDateTime (date?: string | null): string {
    if (!date) return 'No date';
    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(new Date(date));
}

function formatCommentAuthorName (authorName: string): string {
    return authorName === 'me' ? 'Me' : authorName;
}

function isCommentEdited (comment: IDocumentComment): boolean {
    return !!comment.updatedAt;
}
</script>

<style scoped lang="scss">
.comment-body-row { display: flex; align-items: end; gap: var(--defaultSpacing); padding: 0 var(--defaultSpacing); flex-wrap: wrap; }
.comment-body-wrapper { flex: 0 0 max-content; max-width: 100%; min-width: 0; }
.comment-body-wrapper.truncated { position: relative; overflow: hidden; }
.comment.compact .comment-body-wrapper.truncated { max-height: 10rem; }
.comment-body-wrapper.truncated::after { content: ""; position: absolute; right: 0; bottom: 0; left: 0; height: 7rem; background: linear-gradient(to bottom, transparent, var(--neutral0) 70%); pointer-events: none; }
.comment.reply .comment-body-wrapper.truncated::after { background: linear-gradient(to bottom, transparent, var(--neutral100) 70%); }
.comment-body { max-width: 100%; overflow-wrap: anywhere; word-break: normal; }
.comment-body :deep(ul), .comment-body :deep(ol) { margin-bottom: var(--defaultSpacing); padding-left: var(--extraLargeSpacing); }
.comment-body :deep(ul) { list-style: disc; }
.comment-body :deep(ol) { list-style: decimal; }
.comment-body :deep(li) { margin-bottom: var(--smallSpacing); }
.comment-body :deep(p), .comment-body :deep(li), .comment-body :deep(a), .comment-body :deep(span) { overflow-wrap: anywhere; word-break: normal; }
.comment-body :deep(img) { display: block; max-width: 100%; height: auto; margin: var(--defaultSpacing) 0; border-radius: var(--borderRadiusInner); }
.comment-view-full { display: inline-flex; align-items: center; gap: var(--smallSpacing); flex: 0 0 auto; border: 0; background: transparent; color: var(--actionColour); cursor: pointer; font-size: var(--smallFontSize); font-weight: var(--fontWeightBold); }
.comment-view-full:hover, .comment-view-full:focus-visible { color: var(--accentColour); outline: none; }
.comment-controls-spacer { flex: 1 1 auto; min-width: var(--defaultSpacing); }
.comment-controls-row { display: flex; align-items: center; gap: var(--smallSpacing); flex: 0 0 auto; margin-left: auto; }
.comment-vote-control { flex: 0 0 auto; }
.comment { padding: var(--defaultSpacing); border: 1px solid var(--neutral400); border-radius: var(--borderRadiusOuter); background: var(--neutral0); }
.comment.group-parent { border: 0; border-radius: 0; background: transparent; }
.comment.compact { padding: var(--smallSpacing); }
.comment.reply { margin: var(--defaultSpacing); border-color: var(--neutral300); background: var(--neutral100); }
.comment-meta { display: flex; align-items: center; gap: var(--smallSpacing); flex-wrap: wrap; justify-content: space-between; margin: 0 var(--defaultSpacing) var(--defaultSpacing); padding-bottom: var(--smallSpacing); border-bottom: 1px solid var(--neutral300); color: var(--neutral700); font-size: var(--smallFontSize); }
.comment-date-meta { display: inline-flex; align-items: center; gap: var(--smallSpacing); }
.comment-edited { color: var(--neutral600); font-style: italic; }
.comment-meta-actions { display: grid; align-items: center; height: 1.75rem; justify-items: end; }
.comment-meta-actions > * { grid-area: 1 / 1; transition: opacity 0.5s ease, transform 0.5s ease; }
.comment-action-controls { display: flex; align-items: center; gap: var(--smallSpacing); opacity: 0; pointer-events: none; transform: translateY(-0.2rem); z-index: 1; }
.comment-action-button { display: inline-flex; align-items: center; justify-content: center; width: 1.75rem; height: 1.75rem; padding: 0; border: 0; border-radius: 50%; background: transparent; color: var(--actionColour); cursor: pointer; }
.comment-action-button:hover, .comment-action-button:focus-visible { background: var(--neutral300); color: var(--accentColour); outline: none; }
.comment-action-button:disabled { cursor: wait; opacity: 0.5; }
.comment.reply:hover > .comment-meta .comment-meta-actions.has-actions .comment-action-controls, .comment.reply:focus-within > .comment-meta .comment-meta-actions.has-actions .comment-action-controls { opacity: 1; pointer-events: auto; transform: translateY(0); }
.comment:hover:not(:has(> .comment.reply:hover)):not(:has(> .comment.reply:focus-within)) > .comment-meta .comment-meta-actions.has-actions .comment-action-controls, .comment:focus-within:not(:has(> .comment.reply:hover)):not(:has(> .comment.reply:focus-within)) > .comment-meta .comment-meta-actions.has-actions .comment-action-controls { opacity: 1; pointer-events: auto; transform: translateY(0); }
.comment:hover:not(:has(> .comment.reply:hover)):not(:has(> .comment.reply:focus-within)) > .comment-meta .comment-meta-actions.has-actions .comment-date-meta, .comment:focus-within:not(:has(> .comment.reply:hover)):not(:has(> .comment.reply:focus-within)) > .comment-meta .comment-meta-actions.has-actions .comment-date-meta, .comment.reply:hover > .comment-meta .comment-meta-actions.has-actions .comment-date-meta, .comment.reply:focus-within > .comment-meta .comment-meta-actions.has-actions .comment-date-meta { opacity: 0; pointer-events: none; transform: translateY(0.2rem); }
.comment:focus-visible { outline: 2px solid var(--actionColour); outline-offset: 2px; }
</style>
